import React from 'react';
import { PaginatedMuiTable } from '@lendica/components';
import { StatusChip } from '@lendica/components';
import {
    currencyFormatter,
    percentageFormatter,
    dateFormatter,
    invoiceOrDealStatusToLabel,
    DealDisplayStatus,
} from '@lendica/utils';
import { useCompanyNavigate } from '@lendica/hooks';
import { useDrawDownApi } from '../api';

const columns = [
    {
        id: 'funded_date',
        label: 'Date',
        CellContent: ({ value }) => {
            return <>{dateFormatter(value, false)}</>;
        },
        tableCellProps: {
            style: {
                fontSize: '13px',
            },
        },
    },
    {
        id: 'amount',
        label: 'Amount',
        formatter: value => currencyFormatter(value),
        tableCellProps: {
            align: 'right',
            padding: 'none',
            style: {
                fontSize: '13px',
            },
        },
    },
    {
        id: 'paid',
        label: 'Paid',
        formatter: value => percentageFormatter(value, 0),
        tableCellProps: {
            align: 'right',
            style: {
                fontSize: '13px',
            },
        },
    },
    {
        id: 'deal_status',
        label: 'Status',
        CellContent: ({ value }) => {
            return (
                <StatusChip
                    status={invoiceOrDealStatusToLabel[value]}
                    sx={{
                        '& .MuiChip-label': {
                            fontSize: '12.5px',
                            maxWidth: '120px',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                        },
                    }}
                    deleteIcon={<></>}
                />
            );
        },
        tableCellProps: {
            width: 120,
            align: 'left',
            padding: 'none',
            style: {
                fontSize: '13px',
            },
        },
    },
];

export const PaginatedDealsTable = () => {
    const navigate = useCompanyNavigate();
    const api = useDrawDownApi();

    const fetchDeals = React.useCallback(
        async ({ page, pageSize }) => {
            const { count, next, previous, results } = await api.getDealsList(page, pageSize);
            const rows = results.map(
                ({
                    id,
                    funded_date,
                    product_name,
                    total_collected,
                    payback_amount,
                    deal_status,
                    gross_funded_amount,
                    status,
                }) => ({
                    id,
                    funded_date,
                    product: product_name,
                    amount: gross_funded_amount,
                    paid: payback_amount ? total_collected / payback_amount : 0,
                    deal_status: status === DealDisplayStatus.Rejected ? status : deal_status,
                })
            );
            return { count, next, previous, results: rows };
        },
        [api]
    );

    const handleTableRowClick = row => {
        navigate(`/drawdown/deal/${row.id}`);
    };

    return (
        <PaginatedMuiTable
            fetcher={fetchDeals}
            columns={columns}
            getRowKey={r => r.id}
            initialPageSize={5}
            pageSizeOptions={[5, 10, 15]}
            syncUrlParams
            onRowClick={handleTableRowClick}
            renderLoading={false}
        />
    );
};
